import { PolicyTest, RuleTest, ScriptKeysLookup } from '@/types'
import * as testValueUtils from '@/utils/testValueUtils'
import { Policy, Rule } from '@klarna/cbrms-policy-rules-manager-client-ts'
import omit from 'lodash/omit'

/**
 * Removes references to invalid helpers and inputs from Policy
 * @param policy
 * @param onClean Called with a message if some cleaning was done.
 */
export const cleanAwayInvalidKeysFromPolicyRules = (
  policy: Policy,
  scriptKeysLookup: ScriptKeysLookup,
  onClean: (msg: string) => void
) => {
  let cleanedPolicy = { ...policy }

  cleanedPolicy.rules = policy.rules.map(rule => {
    const { invalidInputs, invalidHelpers, isCorrupt } = findInvalidInputsAndHelpers(scriptKeysLookup, rule)

    if (isCorrupt) {
      let msg = `${rule.id} contained legacy references to removed inputs or helpers.`
      if (invalidInputs.length > 0) {
        msg += `\nRemoving input references: ${invalidInputs.join(', ')}`
      }

      if (invalidHelpers.length > 0) {
        msg += `\nRemoving helper function references: ${invalidHelpers.join(', ')}`
      }

      msg += '  \nNote the script may still refer to them, please correct it.'

      onClean(msg)
    }

    return isCorrupt ? removeInputAndHelperReferences({ inputs: invalidInputs, helpers: invalidHelpers }, rule) : rule
  })

  const { isCorrupt, invalidHelpers, invalidInputs } = findInvalidInputsAndHelpers(scriptKeysLookup, cleanedPolicy)

  cleanedPolicy = isCorrupt
    ? removeInputAndHelperReferences({ inputs: invalidInputs, helpers: invalidHelpers }, cleanedPolicy)
    : cleanedPolicy

  return cleanedPolicy
}

export const findInvalidInputsAndHelpers = <T extends Rule | Policy>(
  scriptKeysLookup: ScriptKeysLookup,
  ruleOrPolicy: T
) => {
  const allReferencedInputs = new Set(ruleOrPolicy.content_inputs || [])
  const allReferencedHelpers = new Set(ruleOrPolicy.content_helpers || [])

  const invalidInputs = [...allReferencedInputs].filter(input => !scriptKeysLookup.inputs[input])
  const invalidHelpers = [...allReferencedHelpers].filter(helper => !scriptKeysLookup.helpers[helper])

  return {
    invalidInputs,
    invalidHelpers,
    isCorrupt: invalidInputs.length > 0 || invalidHelpers.length > 0,
  }
}

export const removeInputAndHelperReferences = <T extends Rule | Policy>(
  { inputs, helpers }: { inputs: string[]; helpers: string[] },
  ruleOrPolicy: T
): T => {
  const fixed = { ...ruleOrPolicy }
  // Remove from metadata
  fixed.content_helpers = (fixed.content_helpers || []).filter(helper => !helpers.includes(helper))

  fixed.content_inputs = (fixed.content_inputs || []).filter(input => !inputs.includes(input))

  // Remove from unit tests
  fixed.unittests = fixed.unittests.map(<K extends RuleTest | PolicyTest>(test: K) => {
    const finalInputs = omit(test.inputs, inputs)
    const finalHelpers = omit(test.helpers, helpers)
    return {
      ...test,
      inputs: finalInputs,
      helpers: finalHelpers,
    } as K
  })
  return fixed
}

/**
 * This migration function should be applied to any Rule which will be edited & saved in the UI.
 * For now this should only be those fetched vi GET_CURRENT_RULE.
 *
 * Any particular migration code can be removed once there remains no data with the old version anymore.
 */
export const migrateRule = (scriptKeysLookup: ScriptKeysLookup, rule: Rule): Rule => {
  const formatVersion = rule.format_version || 0

  const migratedRule = {
    ...rule,
  }

  // Make sure rule tests have native data types
  if (formatVersion < 1) {
    migratedRule.unittests = migratedRule.unittests.map(test =>
      testValueUtils.testWithNativeValueTypes(scriptKeysLookup, test)
    )
    migratedRule.format_version = 1
  }

  // Example of adding another migration:
  // if (formatVersion < 2) {
  //  migratedRule.something = convertSomething(migratedRule.something);
  //  migratedRule.format_version = 2;
  // }

  return migratedRule
}

/**
 * This migration function should be applied to any Policy which will be edited & saved in the UI.
 * For now this should only be those fetched vi GET_CURRENT_POLICY.
 *
 * Any particular migration code can be removed once there remains no data with the old version anymore.
 */
export const migratePolicy = (scriptKeysLookup: ScriptKeysLookup, policy: Policy): Policy => {
  const formatVersion = policy.format_version || 0
  const migratedPolicy = {
    ...policy,
  }

  // Make sure policy tests have native data types
  if (formatVersion < 1) {
    migratedPolicy.unittests = migratedPolicy.unittests.map(test =>
      testValueUtils.testWithNativeValueTypes(scriptKeysLookup, test)
    )
    migratedPolicy.format_version = 1
  }

  return migratedPolicy
}
